import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Flex, Switch } from 'antd';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { collect } from 'collect.js';
import { pick } from 'lodash';
import Metric from '../../../../components/stats/Metric';
import MetricsContainer from '../../../../components/stats/MetricsContainer';
import HelperMethods from '../../../../services/exports/HelperMethods';
import useApiClient from '../../../../hooks/api/useApiClient';
import { useGetCustomerSuccessStatsQuery, useUpdateServiceProviderMutation } from '../../../../store/api-slices';
import DrawerDetails from './DrawerDetails';
import usePermissions from '../../../../hooks/auth/usePermissions';
import currentServiceProviderSlice from '../../../../store/slices/CurrentServiceProviderSlice';

const filters = [
  {
    id: 'filter_period_today',
    period: 'today',
  },
  {
    id: 'filter_last_7_days',
    name: 'Last 7 days',
    start_date: moment().subtract(7, 'days').startOf('day').format(),
    end_date: moment().endOf('day').format(),
  },
  {
    id: 'whole_month',
    name: 'Whole month',
    start_date: null,
    end_date: null,
  },
];

function CustomerSuccessTeam({ dateRangeFilter, renderDateRangeFilter }) {
  const [month, setMonth] = useState(() => dayjs());

  const selectedFilter = useMemo(
    () =>
      dateRangeFilter
        ? collect(filters).first(
            (item) =>
              (item.period !== undefined && item.period === dateRangeFilter.period) ||
              (item.start_date !== undefined &&
                item.start_date === dateRangeFilter.start_date &&
                item.end_date === dateRangeFilter.end_date),
          )
        : null,
    [filters, dateRangeFilter],
  );

  return (
    <>
      <div className="space-y-4">
        <Flex gap="middle">
          <DatePicker
            value={month}
            onChange={setMonth}
            allowClear={false}
            picker="month"
            format="MMMM YYYY"
            className="min-w-[160px] h-fit my-auto"
          />
          {renderDateRangeFilter({
            excludeFilters: ['*'],
            customFilters: filters,
          })}
        </Flex>
        <PublishUnpublish />
      </div>
      <Metrics selectedFilter={selectedFilter} month={month} />
    </>
  );
}

function PublishUnpublish() {
  const { t } = useTranslation(null, { keyPrefix: 'Views:Dashboard:ServiceProvider:CustomerSuccessTeam' });

  const { isAdmin } = usePermissions();
  const isPublished = useSelector(
    (state) => state.currentServiceProvider.serviceProvider.is_customer_success_metrics_published,
  );
  const { ServiceProvidersManager } = useApiClient();
  const [update, { isLoading }] = useUpdateServiceProviderMutation();
  const dispatch = useDispatch();

  if (!isAdmin) {
    return null;
  }

  const onChange = async (checked) => {
    const response = await update({
      apiManager: ServiceProvidersManager,
      is_customer_success_metrics_published: checked,
    });

    if (response.error) {
      toast.error('Failed. Please try again later.');

      return;
    }

    dispatch(currentServiceProviderSlice.actions.update(response.data));
    toast.success('Successfully updated.');
  };

  const copy = t(isPublished ? 'unpublish' : 'publish');

  return (
    <Flex className="gap-2">
      <Switch loading={isLoading} checked={isPublished} id="publish" onChange={onChange} />
      <label htmlFor="publish">{copy}</label>
    </Flex>
  );
}

function Metrics({ selectedFilter, month }) {
  const { t } = useTranslation(null, { keyPrefix: 'Views:Dashboard:ServiceProvider:CustomerSuccessTeam.metrics' });

  const { StatsManager } = useApiClient();

  const formattedMonth = month.format('YYYY-MM-DD');
  const { data, isFetching } = useGetCustomerSuccessStatsQuery({
    apiManager: StatsManager,
    month: formattedMonth,
    ...pick(selectedFilter ?? {}, ['period', 'start_date', 'end_date']),
  });

  const getItem = (item) => {
    if (!item) {
      return;
    }

    return {
      value: item.current,
      previous: item.prev,
      change: item.prev ? HelperMethods.percentagePart(item.current - item.prev, item.prev) : null,
    };
  };

  function detailsModalRenderer(metric) {
    return function ({ onClose }) {
      return <DrawerDetails metric={metric} month={month} onClose={onClose} />;
    };
  }

  const nrrCurrentMonth = useMemo(() => {
    const date = moment(month.toDate());

    if (date.isSameOrAfter(moment(), 'month')) {
      return date.subtract(1, 'month');
    }

    return date;
  }, [month]);
  const nrrPrevMonth = moment(month.toDate()).subtract(1, 'quarter').endOf('quarter');

  const metrics = collect([
    {
      key: 'nrr',
      items: {
        value: data?.data?.nrr,
      },
      formatter: HelperMethods.formatPercentage,
      name: t('nrr.title'),
      description: t('nrr.description', {
        currentMonth: nrrCurrentMonth.format('MMMM'),
        currentQuarter: nrrCurrentMonth.quarter(),
        prevMonth: nrrPrevMonth.format('MMMM'),
        prevQuarter: nrrPrevMonth.quarter(),
      }),
    },
    { key: 'churn', flipChange: true, hasDetails: true },
    { key: 'gte_c_tier', hasDetails: true },
    { key: 'upgrades', hasDetails: true },
    { key: 'downgrades', hasDetails: true },
    { key: 'first_time_500_monthly_sales', hasDetails: true },
    { key: '14_days_no_sales', hasDetails: true },
    { key: 'offers_turned_on', hasDetails: true },
    { key: 'flyers_sent' },
    { key: 'on_google_ads_trial', hasDetails: true },
    { key: 'converted_google_ads', hasDetails: true },
  ]).when(selectedFilter?.id && selectedFilter.id !== 'whole_month', (collection) =>
    collection.whereIn('key', ['churn', 'offers_turned_on', 'flyers_sent']),
  );

  return (
    <MetricsContainer
      metrics={metrics.map(({ key: metric, hasDetails, ...rest }) => (
        <Metric
          loading={isFetching}
          key={metric}
          name={t(metric)}
          items={getItem(data?.data?.[metric])}
          excludedHistoryMetrics={['*']}
          renderMetricDetails={hasDetails && data?.data?.[metric]?.current > 0 ? detailsModalRenderer(metric) : null}
          {...rest}
        />
      ))}
    />
  );
}

export default CustomerSuccessTeam;
